import { __decorate } from "tslib";
import { getDateFormatYM, getDateFormatYMD } from '@/utils/date';
import { getBarOptions, getLineOption } from '@/service/CompanyBase/CompanyDataAnalysis/echartsOption';
import { GRANULARITY } from '@/apis/service/account/AccountDataAnalysisApi.type';
import _ from 'lodash';
import { injectable } from 'inversify';
/**
 * 基础图表服务类
 * 提取图表 option 的公共部分
 */
let BaseDataAnalysisChartService = class BaseDataAnalysisChartService {
    constructor() {
        this.granularity = GRANULARITY.DAY;
    }
    /**
     * 获取日期格式
     */
    getDateFormat(date) {
        if (this.granularity === GRANULARITY.MONTH) {
            return getDateFormatYM(date);
        }
        return getDateFormatYMD(date);
    }
    /**
     * 计算比例
     */
    getRatio(value, total) {
        return total === 0 ? 0 : parseFloat((value / total * 100).toFixed(2));
    }
    /**
     * 获取柱状图系列配置
     */
    getBarOptionSeries(index, formatter) {
        return {
            type: 'bar',
            label: {
                show: true,
                position: 'top',
                formatter: params => formatter ? formatter(params.value[index]) : this.getLineBarValueFormatter(params.value[index], 'bar'),
            },
        };
    }
    /**
     * 数值格式化器
     */
    getLineBarValueFormatter(value, type) {
        return type === 'line' ? value + '%' : value < 1000 ? value : (value / 1000).toFixed(2) + 'k';
    }
    /**
     * 获取带缩放功能的图表配置
     */
    getZoomChartOption(option) {
        option.dataZoom = [
            {
                type: 'slider',
                start: 0,
                end: 50,
                height: '15',
                bottom: '10%',
            },
            {
                type: 'inside',
                start: 0,
                end: 50,
            }
        ];
        option.grid = {
            top: '8%',
            left: '3%',
            right: '4%',
            bottom: '18%',
            containLabel: true,
        };
        return option;
    }
    /**
     * 取消缩放功能的图表配置
     */
    cancelZoomChartOption(option) {
        option.dataZoom = [];
        option.grid = {
            left: '3%',
            right: '4%',
            bottom: '8%',
            top: '8%',
            containLabel: true,
        };
        return option;
    }
    /**
     * 根据数据长度决定是否启用缩放
     */
    getChartOptionWithZoom(option, dataLength) {
        if (dataLength > 30) {
            return this.getZoomChartOption(option);
        }
        else {
            return this.cancelZoomChartOption(option);
        }
    }
    /**
     * 获取基础折线图配置
     */
    getLineChartOption(dates, series) {
        const options = getLineOption();
        _.assign(options.xAxis, { data: dates });
        return {
            ...options,
            series,
        };
    }
    /**
     * 获取基础柱状图配置
     */
    getBarChartOption(dates, series, colorList) {
        const options = getBarOptions();
        if (colorList) {
            options.color = colorList;
        }
        options.series = series;
        _.assign(options.xAxis, { data: dates });
        return options;
    }
    /**
     * 生成数据键值映射
     */
    generateKeysMap(sortList, dataLength) {
        return _.zipObject(sortList, _.times(sortList.length, () => new Array(dataLength).fill(0)));
    }
    /**
     * 处理数据分析表格行
     */
    getAnalysisTableRows(data, dataSet) {
        return data.map(item => {
            const row = {};
            _.forEach(Object.keys(item), key => {
                row[key] = item[key][dataSet];
                _.assign(row, { date: this.getDateFormat(item[key].event_date) });
            });
            return row;
        });
    }
    /**
     * 更新堆叠图的总计标签
     */
    updateTotalLabels(chart, selectedList) {
        const option = chart.getOption();
        if (!option.series[0].data)
            return;
        // 计算每个类别的总和
        const dataIndexes = option.series[0].data.map((_, index) => index);
        const totals = dataIndexes.map(index => {
            let total = 0;
            option.series.forEach(series => {
                if (selectedList[series.name]) {
                    const count = series.data[index] || 0;
                    total += count;
                }
            });
            return total;
        });
        // 计算最后一个展示的key
        const lastTrueKey = [...Object.keys(selectedList)].reverse().find(key => selectedList[key]);
        // 设置顶部系列的标签为总和
        option.series.forEach(series => {
            series.label = {
                show: series.name === lastTrueKey,
                position: 'top',
                formatter: params => this.getLineBarValueFormatter(totals[params.dataIndex], 'bar'),
            };
        });
        chart.setOption(option);
    }
    /**
     * 获取带相同值显示的堆叠图系列
     */
    getStackBarSeriesSameValue(option, name) {
        const tpOption = _.cloneDeep(option);
        _.forEach(tpOption.series, series => {
            if (series.name === name) {
                series.label = {
                    show: true,
                    formatter: params => this.getLineBarValueFormatter(params.value, 'bar'),
                    emphasis: {
                        show: true,
                    },
                };
            }
        });
        return tpOption;
    }
    /**
     * 生成堆叠图系列配置
     */
    generateStackBarSeries(keysMap, columns, total, isLastColumn) {
        return Object.entries(keysMap).reduce((arr, [key, value]) => {
            arr.push({
                name: (_.find(columns, { field: key }))?.label || key,
                type: 'bar',
                stack: 'total',
                emphasis: { focus: 'series' },
                data: value,
                label: {
                    show: isLastColumn(key),
                    position: 'top',
                    formatter: params => this.getLineBarValueFormatter(total[params.dataIndex], 'bar'),
                },
            });
            return arr;
        }, []);
    }
    /**
     * 生成折线图系列配置
     */
    generateLineSeries(keysMap, columns) {
        return Object.entries(keysMap).reduce((arr, [key, value]) => {
            arr.push({
                name: (_.find(columns, { field: key }))?.label || key,
                type: 'line',
                emphasis: { focus: 'series' },
                data: value,
                label: {
                    show: true,
                    position: 'top',
                    formatter: params => this.getLineBarValueFormatter(params.value, 'bar'),
                },
            });
            return arr;
        }, []);
    }
    /**
     * 计算堆叠图总计
     */
    calculateStackTotal(keysMap) {
        const total = [];
        for (const key in keysMap) {
            if (Object.prototype.hasOwnProperty.call(keysMap, key)) {
                keysMap[key].forEach((value, index) => {
                    const count = value || 0;
                    total[index] = (total[index] || 0) + count;
                });
            }
        }
        return total;
    }
};
BaseDataAnalysisChartService = __decorate([
    injectable()
], BaseDataAnalysisChartService);
export { BaseDataAnalysisChartService };
